// THE GOLDEN GLINT — an opportunity to spot. Every so often a glint appears on a row for GlintConfig.windowMs; a paw click while it
// is up CATCHES it, and the next boostClicks clicks pay × boostMult ("hot hand" — the catching click included). Scheduling: the
// first glint firstAfterMs after the game starts, then a random gap in [everyMinMs, everyMaxMs] after each glint ends (caught or
// not). The rules are pure (createGlint — tests: scripts/qa/glint.test.mjs); init() runs them on a clock and talks in events:
// GlintUp (the scene shows it on a row), GlintGone, GlintCaught. seasonManager asks takeMult() for each paw click.

import { Events, emit } from '../ui/eventBus.js'
import { GlintConfig } from '../config/balance.js'

export function createGlint(cfg, { random = Math.random } = {}) {
  let upSince = null, nextAt = null, boostLeft = 0
  const gap = () => cfg.everyMinMs + random() * (cfg.everyMaxMs - cfg.everyMinMs)
  const isUp = (now) => upSince != null && now - upSince < cfg.windowMs
  return {
    start(now) { nextAt = now + cfg.firstAfterMs; upSince = null },
    isUp,
    // What the clock should do now: 'due' (show a glint — then call up()), 'gone' (the window passed, hide it), or null.
    due(now, canSpawn = true) {
      if (nextAt == null) this.start(now)
      if (upSince != null && now - upSince >= cfg.windowMs) { upSince = null; nextAt = now + gap(); return 'gone' }
      return upSince == null && now >= nextAt && canSpawn ? 'due' : null
    },
    up(now) { upSince = now },
    // A paw click: caught when the glint is up — the boost begins. Returns true when caught.
    click(now) {
      if (!isUp(now)) return false
      upSince = null; nextAt = now + gap(); boostLeft = cfg.boostClicks
      return true
    },
    // The multiplier for THIS click (uses up one boosted click).
    takeMult() { if (boostLeft > 0) { boostLeft -= 1; return cfg.boostMult } return 1 },
    boostLeft: () => boostLeft,
    state: (now) => ({ up: isUp(now), upForMs: upSince == null ? 0 : now - upSince, boostLeft, nextInMs: nextAt == null ? null : Math.max(0, nextAt - now) }),
  }
}

// ── the game's glint ─────────────────────────────────────────────────────────────────────────────────────────────
const glint = createGlint(GlintConfig)
let timer = null, canSpawnFn = () => true, suspended = false // suspended: dev/test — no glint comes by itself

export function init({ canSpawn = () => true } = {}) {
  canSpawnFn = canSpawn
  glint.start(Date.now())
  if (timer) clearInterval(timer)
  timer = setInterval(tick, 250)
  if (import.meta.env?.DEV) window.__glint = { spawn: () => { glint.up(Date.now()); emit(Events.GlintUp, {}) }, state: () => glint.state(Date.now()), suspend: (on) => { suspended = !!on }, cfg: GlintConfig }
}
function tick() {
  if (suspended) return
  const now = Date.now()
  const r = glint.due(now, !!canSpawnFn())
  if (r === 'due') { glint.up(now); emit(Events.GlintUp, {}) }
  else if (r === 'gone') emit(Events.GlintGone, {})
}
// seasonManager, on a paw click: did this click catch a glint? (emits GlintCaught)
export function onPawClick(now = Date.now()) {
  if (!glint.click(now)) return false
  emit(Events.GlintCaught, { boostClicks: GlintConfig.boostClicks, boostMult: GlintConfig.boostMult })
  return true
}
export const takeMult = () => glint.takeMult()
export const boostLeft = () => glint.boostLeft()
export const isUp = (now = Date.now()) => glint.isUp(now)
